import { ReactNode, useRef } from 'react';

import { AnimatedNode } from '@ui/components/Animation/AnimatedNode';
import { AnimationCrossfade } from '@ui/components/AnimationCrossfade';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { PageComplexSubTitle } from '@ui/components/PageComplexSubTitle';
import { PageSubTitle } from '@ui/components/PageSubTitle';
import { Tabs } from '@ui/components/Tabs';

import { useTournamentCtx } from '@features/tournament/providers/TournamentProvider';
import {
  ResultsTableTabs,
  ResultsTableWidget,
  useResultsTableSortTabs,
} from '@features/tournament/ui/components/ResultsTable';
import { RoundMovementWidget } from '@features/tournament/ui/components/RoundMovement/RoundMovementWidget';
import { StartListWidget } from '@features/tournament/ui/components/StartList/StartListWidget';

import styles from './TournamentResultsWidget.module.css';

type TabValue = 'table' | 'rounds' | 'list' | 'teams';

type Tab = { value: TabValue; label: ReactNode };

const COMMON_DESKTOP_TABS: Tab[] = [
  { value: 'table', label: 'Турнирная таблица' },
  { value: 'rounds', label: 'Движение по турам' },
  { value: 'list', label: 'Стартовый лист' },
];
const COMMON_MOBILE_TABS: Tab[] = [
  { value: 'table', label: 'Таблица' },
  { value: 'rounds', label: 'По турам' },
  { value: 'list', label: 'Лист' },
];

const PRIVATE_TEAM_DESKTOP_TABS: Tab[] = [
  { value: 'table', label: 'Личный зачет' },
  { value: 'teams', label: 'Командный зачет' },
  { value: 'rounds', label: 'Движение по турам' },
  { value: 'list', label: 'Стартовый лист' },
];
const PRIVATE_TEAM_MOBILE_TABS: Tab[] = [
  { value: 'table', label: 'Личн.' },
  { value: 'teams', label: 'Ком.' },
  { value: 'rounds', label: 'По турам' },
  { value: 'list', label: 'Лист' },
];

const KNOCKOUT_DESKTOP_TABS: Tab[] = [
  { value: 'table', label: 'Общая таблица' },
  { value: 'rounds', label: 'По этапам' },
  { value: 'list', label: 'Стартовый лист' },
];

const KNOCKOUT_MOBILE_TABS: Tab[] = [
  { value: 'rounds', label: 'По этапам' },
  { value: 'list', label: 'Стартовый лист' },
];

/**
 * Виджет выбирает, показывать таблицу или движение по раундам в результатах
 */
export function TournamentResultsWidget({
  resultsTab,
  onResultsTabChange,
}: {
  resultsTab: TabValue;
  onResultsTabChange: (resultsTab: TabValue) => void;
}) {
  const isMobile = useMediaQuery() === 'mobile';

  const { state } = useTournamentCtx();
  const { tournament } = state;

  const isSeance = tournament?.system === 'Seance';
  const isTournament = tournament?.system !== 'OneOnOne' && !isSeance;
  const isKnockoutTournament = tournament?.system === 'Knockout';

  let tabs = isMobile
    ? isKnockoutTournament
      ? KNOCKOUT_MOBILE_TABS
      : COMMON_MOBILE_TABS
    : isKnockoutTournament
      ? KNOCKOUT_DESKTOP_TABS
      : COMMON_DESKTOP_TABS;

  if (tournament?.mode === 'PrivateTeam') {
    tabs = isMobile ? PRIVATE_TEAM_MOBILE_TABS : PRIVATE_TEAM_DESKTOP_TABS;
  }

  const show =
    // не показываем вкладку с таблицей на мобилке
    isMobile && isKnockoutTournament && resultsTab === 'table' ? 'rounds' : resultsTab;

  const { sort, setSort, showSortTabs } = useResultsTableSortTabs(show);

  const sortTabsWasHidden = useRef(!showSortTabs);
  if (!showSortTabs && !sortTabsWasHidden.current) {
    sortTabsWasHidden.current = true;
  }

  if (isSeance) {
    return (
      <div className={styles.results}>
        <PageSubTitle className={styles.title} variant="new">
          Результаты
        </PageSubTitle>
        <RoundMovementWidget />
      </div>
    );
  }

  return (
    <div className={styles.results}>
      <PageComplexSubTitle
        className={styles.title}
        title="Участники"
        filters={
          isTournament ? (
            <Tabs
              className={styles.tabs}
              tabClassName={styles.tab}
              flat
              tabs={tabs}
              value={show}
              onChange={onResultsTabChange}
            />
          ) : undefined
        }
        subfilters={
          <AnimatedNode animation="crossfade-synced" initial={sortTabsWasHidden.current}>
            {showSortTabs ? (
              <ResultsTableTabs className={styles.sortTabs} value={sort} onChange={setSort} />
            ) : null}
          </AnimatedNode>
        }
      />

      <AnimationCrossfade contentKey={show} timeMs={300}>
        {show === 'teams' && <ResultsTableWidget showTeams sort={sort} />}
        {show === 'table' && <ResultsTableWidget sort={sort} />}
        {show === 'rounds' && <RoundMovementWidget />}
        {show === 'list' && <StartListWidget />}
      </AnimationCrossfade>
    </div>
  );
}
